import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT, type T } from "@/i18n";
import { FORMATS, type FormatKey } from "@/lib/labelFormats";
import { useAuth } from "@/contexts/AuthContext";
import MediaStrip from "@/components/MediaStrip";
import { shortDay, todayInZurich, useFormat } from "@/lib/format";
import ReturnList, { ReturnMeta, useExpected } from "@/components/ReturnList";
import CompositionEditor, { CompositionBadge, partsOf } from "@/components/CompositionEditor";
import { fetchComposition, saveComposition } from "@/services/productProfiles";
import { uploadMedia } from "@/services/media";
import { shrinkImage } from "@/lib/shrinkImage";
import OrderHint from "@/components/OrderHint";
import PhotoRecognition from "@/components/PhotoRecognition";
import { ReadOnlyArea, ReadOnlyBanner } from "@/components/ReadOnly";
import {
  fetchReturns, fetchReturnItems, searchCatalogue, fetchPieces, fetchPiece, createPieces, updatePiece,
  labelPiece, leavePiece, backPiece, deletePiece, OUT_REASONS, fetchLabelling, fetchLabellingUnits, fetchLabellingTotals, labelReception,
  type Condition, type ReturnEvent, type StockPiece, type LabellingUnit, type LabellingDelivery,
  type ReturnItem, type PieceStats,
} from "@/services/stock";

// Z Furniture · Stock — his "Entrée en stock des meubles de retour client". Intake starts from the
// RETURN: its own furniture list is the short list a piece is identified from.
export default function StockPage({ warehouseId, initialReturnId = null }: { warehouseId: number | null; initialReturnId?: number | null }) {
  const { t } = useT();
  // His four Stock tabs: Entrée, Retours, Étiquetage, Inventaire — the last one used is remembered.
  type View = "intake" | "returns" | "labelling" | "inventory";
  const [view, setViewState] = useState<View>(() => {
    try { const v = localStorage.getItem("myotaku-warehouse:stock-tab"); if (v === "intake" || v === "returns" || v === "labelling" || v === "inventory") return v; } catch { /* storage blocked */ }
    return "intake";
  });
  const setView = (v: View) => { setViewState(v); try { localStorage.setItem("myotaku-warehouse:stock-tab", v); } catch { /* this tab only */ } };
  // His tab badges: pieces in stock here, returns in the window. Shared with the tabs' own lists.
  const held = useQuery({ queryKey: ["stock-pieces", warehouseId, "all"], queryFn: () => fetchPieces(warehouseId, "all"), refetchInterval: 15000 });
  const rets = useQuery({ queryKey: ["stock-returns", warehouseId], queryFn: () => fetchReturns(warehouseId), refetchInterval: 15000 });
  // His Labelling badge (recLeft): the units not yet labelled of this warehouse's deliveries to come
  // or not yet dated — each job's totals asked as its card asks them, so the two share one answer.
  const lab = useQuery({ queryKey: ["labelling", warehouseId], queryFn: () => fetchLabelling(warehouseId), refetchInterval: 15000 });
  const upcoming = (lab.data?.deliveries ?? []).filter((d) => !d.delivery_on || d.delivery_on >= todayInZurich());
  const labTotals = useQueries({ queries: upcoming.map((d) => ({
    queryKey: ["labelling-totals", d.event_id, d.labelled], queryFn: () => fetchLabellingTotals([d.event_id]), staleTime: 60_000 })) });
  const toLabel = lab.data && labTotals.every((q) => q.data)
    ? labTotals.reduce((n, q, i) => { const x = q.data?.totals[String(upcoming[i].event_id)]; return n + (x ? x.total - x.labelled : 0); }, 0)
    : undefined;
  const badge: Partial<Record<View, number>> = {
    inventory: held.data?.pieces.filter((p) => p.status !== "left").length,
    returns: rets.data?.returns.length,
    labelling: toLabel,
  };
  // Arriving from a cart: straight to intake, with that cart's return already chosen.
  useEffect(() => { if (initialReturnId) setView("intake"); }, [initialReturnId]);
  const [labels, setLabels] = useState<StockPiece[] | null>(null);
  const [labelsFor, setLabelsFor] = useState<{ client: string | null; date: string | null } | null>(null);
  // His reception labelling downloads the labels the moment the pieces are labelled.
  const [labelsAuto, setLabelsAuto] = useState(false);
  // His Returns tab: tapping a piece opens Intake with that return and that piece already chosen.
  const [preset, setPreset] = useState<{ returnId: number; pick: Pick; key: number } | null>(null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {(["intake", "returns", "labelling", "inventory"] as const).map((k) => (
          <button key={k} onClick={() => setView(k)}
            className={`rounded-lg px-3 py-1.5 text-sm ${view === k ? "bg-orange-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
            {t(`stock.tab.${k}`)}
            {badge[k] != null && <span className={`ml-1.5 rounded-full px-1.5 text-xs tabular-nums ${view === k ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>{badge[k]}</span>}
          </button>
        ))}
        <p className="ml-2 text-sm text-slate-500">{t("stock.tagline")}</p>
      </div>
      <ReadOnlyBanner module="stock" text={t("stock.readOnly")} />
      {view === "intake" && (
        <ReadOnlyArea module="stock">
          <Intake key={preset?.key ?? 0} warehouseId={warehouseId} initialReturnId={preset?.returnId ?? initialReturnId}
            initialPick={preset?.pick ?? null} onSaved={setLabels} />
        </ReadOnlyArea>
      )}
      {view === "returns" && <ReturnsView warehouseId={warehouseId}
        onEnter={(returnId, pick) => { setPreset({ returnId, pick, key: Date.now() }); setView("intake"); window.scrollTo({ top: 0 }); }} />}
      {view === "labelling" && <LabellingView warehouseId={warehouseId} onLabels={(p, f) => { setLabelsFor(f); setLabelsAuto(true); setLabels(p); }} />}
      {view === "inventory" && <Inventory warehouseId={warehouseId} onLabels={(p) => { setLabelsFor(null); setLabelsAuto(false); setLabels(p); }} />}
      {labels && <LabelSheet pieces={labels} forClient={labelsFor} autoDownload={labelsAuto} onClose={() => { setLabels(null); setLabelsFor(null); }} />}
    </div>
  );
}

// ---- intake: return → furniture → state → wrapping and place → save and label -------------------

type Pick = { productId: number | null; name: string | null; sku: string | null; remaining: number };

function Intake({ warehouseId, initialReturnId, initialPick = null, onSaved }: {
  warehouseId: number | null; initialReturnId: number | null; initialPick?: Pick | null; onSaved: (p: StockPiece[]) => void;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [ret, setRet] = useState<ReturnEvent | "none" | null>(null);
  // The return step 2 belongs to: kept while another return is being chosen, so the photo stays.
  const [lastRet, setLastRet] = useState<ReturnEvent | "none" | null>(null);
  const shownRet = ret ?? lastRet;
  // His warehouse switch clears a return already chosen (it belongs to the other warehouse); "no
  // return" stays. Reset while rendering, so a return auto-picked for the new warehouse is kept.
  const [whSeen, setWhSeen] = useState(warehouseId);
  if (whSeen !== warehouseId) {
    setWhSeen(warehouseId);
    if (ret && ret !== "none") { setRet(null); setLastRet(null); }
  }
  const [pick, setPick] = useState<Pick | null>(null);
  // His intake asks for everything explicitly: no condition, no wrapping is assumed.
  const [condition, setCondition] = useState<Condition | null>(null);
  const [damage, setDamage] = useState("");
  const [damagePhotos, setDamagePhotos] = useState<LocalPhoto[]>([]);
  const [wrapPhoto, setWrapPhoto] = useState<LocalPhoto | null>(null);
  const [blanket, setBlanket] = useState(false);
  const [film, setFilm] = useState(false);
  const [saved, setSaved] = useState<{ pieces: StockPiece[]; photosFailed: number; parts: number } | null>(null);
  const [pallet, setPallet] = useState(() => remembered(warehouseId, "pallet"));
  const [rack, setRack] = useState(() => remembered(warehouseId, "rack"));
  const [qty, setQty] = useState(1);
  // "Reconnaître avec Claude": the recognition whose photo goes with the piece, and — when the
  // product was taken from its answer — how sure it was.
  const [recognitionId, setRecognitionId] = useState<number | null>(null);
  const [byPhoto, setByPhoto] = useState<{ confidence: number | null } | null>(null);
  const [round, setRound] = useState(0);
  const pickByHand = (p: Pick) => { setPick(p); setByPhoto(null); setFromGrid(false); };
  // His `_src === "grid"`: tapped in the return's own list — a photo taken afterwards is for the record.
  const [fromGrid, setFromGrid] = useState(false);
  const pickFromGrid = (p: Pick) => { pickByHand(p); setFromGrid(true); };
  // The piece tapped in the Returns tab, chosen once its return is.
  const presetUsed = useRef(false);
  useEffect(() => {
    if (initialPick && !presetUsed.current && ret && ret !== "none" && ret.id === initialReturnId) { presetUsed.current = true; pickFromGrid(initialPick); }
  }, [ret, initialPick, initialReturnId]);

  useEffect(() => { setPallet(remembered(warehouseId, "pallet")); setRack(remembered(warehouseId, "rack")); }, [warehouseId]);
  useEffect(() => { setQty(Math.max(1, Math.min(20, pick?.remaining || 1))); }, [pick]);
  // His quantity notices (unitPlan): how many the return expects and has, the extra piece, the
  // records a batch makes, and the "Item i/n" numbers the save will give — the same rule as the server.
  const retItems = useQuery({
    queryKey: ["stock-return", shownRet && shownRet !== "none" ? shownRet.id : null],
    queryFn: () => fetchReturnItems((shownRet as ReturnEvent).id), enabled: !!shownRet && shownRet !== "none",
  });
  const notices = (() => {
    if (!pick?.productId) return [] as string[];
    const it = ret && ret !== "none" ? retItems.data?.items.find((i) => i.product_id === pick.productId) : undefined;
    const q = it ? Number(it.expected) : 0;
    const k = it ? it.entered : 0;
    const total = it ? Math.max(q, k + qty) : qty;
    const start = it ? k + 1 : 1;
    const out: string[] = [];
    if (it && (q > 1 || k > 0)) out.push(k >= q ? t("stock.qtyOver", { q, k }) : t("stock.qtyAuto", { q, k }));
    if (qty > 1) out.push(t("stock.qtyHint", { n: qty }));
    if (total > 1) out.push(t("stock.qtyNums", { list: Array.from({ length: qty }, (_, j) => t("stock.unitOf", { i: start + j, n: total })).join(", ") }));
    return out;
  })();
  const compo = useQuery({ queryKey: ["composition", pick?.productId], queryFn: () => fetchComposition(pick!.productId!), enabled: !!pick?.productId });
  const parts = partsOf(t, compo.data).length;
  const needDamage = condition === "light_marks" || condition === "damaged";

  const save = useMutation({
    mutationFn: async () => {
      // His save: a composition only suggested and kept as shown is confirmed for the product.
      if (pick?.productId && compo.data?.suggested) {
        await saveComposition(pick.productId, partsOf(t, compo.data));
        void qc.invalidateQueries({ queryKey: ["composition", pick.productId] });
      }
      const r = await createPieces({
        warehouse_id: warehouseId, myotaku_product_id: pick?.productId, source: "return",
        product_name: pick?.name, product_sku: pick?.sku,
        myotaku_return_event_id: ret && ret !== "none" ? ret.id : null,
        // His `retour` copied onto the record: the return as the planning shows it now.
        ...(ret && ret !== "none" ? { return_title: ret.title, return_client: ret.client_name || ret.contract_name,
          return_date: ret.starts_at, return_type: ret.type_label } : {}),
        myotaku_contract_id: ret && ret !== "none" ? ret.contract_id : null,
        condition, damage_note: needDamage ? damage : null, wrapped_in_blanket: blanket, wrapped_in_film: film,
        pallet, rack, qty,
        identified_by: byPhoto ? "photo" : "person", match_confidence: byPhoto?.confidence ?? null,
        recognition_id: recognitionId,
      });
      // His damage photos and the photo of the wrapped piece go with every record of the batch.
      const photos: [LocalPhoto, "damage" | "wrapped"][] = [...(needDamage ? damagePhotos.map((p) => [p, "damage"] as [LocalPhoto, "damage"]) : []),
        ...(wrapPhoto ? [[wrapPhoto, "wrapped"] as [LocalPhoto, "wrapped"]] : [])];
      let photosFailed = 0;
      for (const [photo, role] of photos) {
        const file = await shrinkImage(photo.file).catch(() => photo.file);
        for (const piece of r.pieces) await uploadMedia("stock_piece", piece.id, file, role).catch(() => { photosFailed++; });
      }
      return { pieces: r.pieces, photosFailed };
    },
    onSuccess: ({ pieces, photosFailed }) => {
      remember(warehouseId, "pallet", pallet); remember(warehouseId, "rack", rack);
      void qc.invalidateQueries({ queryKey: ["stock-returns"] });
      void qc.invalidateQueries({ queryKey: ["stock-return"] });
      void qc.invalidateQueries({ queryKey: ["stock-pieces"] });
      setSaved({ pieces, photosFailed, parts });
      setPick(null); setCondition(null); setDamage(""); setDamagePhotos([]); setWrapPhoto(null); setBlanket(false); setFilm(false);
      setRecognitionId(null); setByPhoto(null); setRound((n) => n + 1);
    },
  });

  if (!warehouseId) return <Empty text={t("stock.pickWarehouse")} />;

  // His "Still needed:" — Save waits for every one of them.
  const missing = [!pick && t("stock.mProduct"), !recognitionId && t("stock.mPhoto"), !condition && t("stock.mCond"),
    needDamage && damagePhotos.length === 0 && t("stock.mDamage"), !(blanket && film) && t("stock.mWrap"),
    !pallet.trim() && t("stock.mPallet"), !rack.trim() && t("stock.mRack")].filter(Boolean) as string[];
  const ready = missing.length === 0;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="space-y-5">
        {saved && (
          <SavedCard saved={saved} onLabels={() => onSaved(saved.pieces)}
            onNextSame={ret && ret !== "none" ? () => setSaved(null) : null}
            onNext={() => { setSaved(null); setRet(null); setLastRet(null); }} />
        )}
        <Step n={1} title={t("stock.s1")} hint={t("stock.s1hint")}>
          {/* His "Changer": another return keeps the photo and the chosen piece (pickRetour). */}
          <ReturnPicker warehouseId={warehouseId} value={ret} autoPickId={initialReturnId}
            onChange={(r) => { setRet(r); if (r) setLastRet(r); }} />
        </Step>
        {shownRet && (
          <div hidden={!ret}>
          <Step n={2} title={t("stock.s2")} hint={shownRet === "none" ? t("stock.s2hintNone") : t("stock.s2hint")}>
            <PhotoRecognition key={round} warehouseId={warehouseId} picked={!!pick} pickedByHand={!!pick && fromGrid} onUnpick={() => setPick(null)}
              returnEventId={shownRet === "none" ? null : shownRet.id} onStarted={setRecognitionId}
              onPick={(p, confidence) => { setPick(p); setByPhoto({ confidence }); setFromGrid(false); }} />
            {/* His grid and manual search fold away once a piece is chosen ("Change" brings them back). */}
            {!pick && shownRet !== "none" && <ReturnFurniture eventId={shownRet.id} selected={null} onPick={pickFromGrid} />}
            {!pick && <CatalogueSearch onPick={pickByHand} returnItems={shownRet !== "none" ? retItems.data?.items ?? [] : []} />}
          </Step>
          </div>
        )}
      </div>

      <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
        <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <p className="text-xs uppercase tracking-wide text-slate-400">{t("stock.chosen")}</p>
          {pick ? (
            <>
              <p className="mt-1 font-medium text-slate-900">{pick.name || `#${pick.productId}`}
                {pick.sku && <span className="ml-2 font-mono text-xs text-slate-500">{pick.sku}</span>}</p>
              {pick.productId && <CompositionEditor productId={pick.productId} productName={pick.name} />}
              {/* His "Changer" (unpick): the grid and the search come back. */}
              <button onClick={() => { setPick(null); setByPhoto(null); }} className="mt-2 rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("com.change")}</button>
            </>
          ) : <p className="mt-1 text-sm text-slate-400">{t("stock.nothingChosen")}</p>}

          <p className="mt-4 text-sm font-medium text-slate-700">{t("stock.s3")}</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {(["good", "light_marks", "damaged"] as const).map((c) => (
              <button key={c} onClick={() => setCondition(c)}
                className={`rounded-lg px-2 py-2 text-sm ring-1 ${condition === c ? conditionTone(c, true) : "bg-white text-slate-700 ring-slate-200"}`}>
                {t(`stock.cond.${c}`)}<span className="block text-[11px] opacity-75">{t(`stock.cond.${c}.hint`)}</span>
              </button>
            ))}
          </div>
          {needDamage && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-700">{t("stock.damagePhotos")} <span className="font-normal text-slate-500">· {t("stock.required")}</span></p>
              <PhotoPicker photos={damagePhotos} onAdd={(p) => setDamagePhotos((l) => [...l, p])} onRemove={(i) => setDamagePhotos((l) => l.filter((_, k) => k !== i))} />
              <label className="mt-2 block text-xs text-slate-500">{t("stock.damageNote")}
                <textarea value={damage} onChange={(e) => setDamage(e.target.value)} rows={2} placeholder={t("stock.damagePh")}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900" />
              </label>
            </div>
          )}

          <p className="mt-4 text-sm font-medium text-slate-700">{t("stock.s4")}</p>
          {parts > 1 && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{t("stock.wrapEach", { n: parts })}</p>}
          <div className="mt-2 flex gap-4 text-sm text-slate-700">
            <label className="flex items-center gap-2"><input type="checkbox" checked={blanket} onChange={(e) => setBlanket(e.target.checked)} />{t("stock.blanket")}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={film} onChange={(e) => setFilm(e.target.checked)} />{t("stock.film")}</label>
          </div>
          <p className="mt-3 text-xs font-semibold text-slate-700">{t("stock.wrapPhoto")} <span className="font-normal text-slate-500">· {t("stock.optional")}</span></p>
          <PhotoPicker photos={wrapPhoto ? [wrapPhoto] : []} max={1} onAdd={setWrapPhoto} onRemove={() => setWrapPhoto(null)} />
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Field label={t("stock.pallet")} value={pallet} onChange={setPallet} placeholder={t("stock.palletPh")} />
            <Field label={t("stock.rack")} value={rack} onChange={setRack} placeholder={t("stock.rackPh")} />
            <label className="text-xs text-slate-500">{t("stock.qty")}
              <input type="number" min={1} max={20} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" />
            </label>
          </div>
          <p className="mt-2 text-xs text-slate-400">{t("stock.placeHint")}</p>
          {notices.length > 0 && (
            <div className="mt-2 space-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700 ring-1 ring-slate-200">
              {notices.map((n) => <p key={n}>{n}</p>)}
            </div>
          )}

          {missing.length > 0 && <p className="mt-3 text-xs text-slate-500">{t("stock.stillNeeded")} {missing.join(" · ")}</p>}
          {save.error && <p className="mt-3 text-sm text-red-600">{(save.error as Error).message}</p>}
          <button disabled={!ready || save.isPending} onClick={() => save.mutate()}
            className="mt-4 w-full rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-40">
            {save.isPending ? "…" : t("stock.save").replace("{n}", String(qty))}
          </button>
        </div>
        <IntakeLabel pick={pick} condition={condition ?? "good"} ret={ret} />
      </aside>
    </div>
  );
}

type LocalPhoto = { file: File; url: string };

// A photo taken on the phone, kept here until the piece is saved (his damage / wrap thumbnails).
function PhotoPicker({ photos, onAdd, onRemove, max }: { photos: LocalPhoto[]; onAdd: (p: LocalPhoto) => void; onRemove: (i: number) => void; max?: number }) {
  const { t } = useT();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {photos.map((p, i) => (
        <span key={p.url} className="relative">
          <img src={p.url} alt="" className="h-16 w-16 rounded-lg object-cover ring-1 ring-slate-200" />
          <button onClick={() => onRemove(i)} aria-label={t("cartx.photoDel")} className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full bg-slate-900 text-xs text-white">×</button>
        </span>
      ))}
      {(!max || photos.length < max) && (
        <button onClick={() => input.current?.click()} className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500">{t("stock.addPhoto")}</button>
      )}
      <input ref={input} type="file" accept="image/*" capture="environment" hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onAdd({ file: f, url: URL.createObjectURL(f) }); }} />
    </div>
  );
}

// His saved screen: what was added, where, and the next step.
function SavedCard({ saved, onLabels, onNextSame, onNext }: {
  saved: { pieces: StockPiece[]; photosFailed: number; parts: number }; onLabels: () => void; onNextSame: (() => void) | null; onNext: () => void;
}) {
  const { t } = useT();
  const first = saved.pieces[0];
  const labels = saved.pieces.length * Math.max(1, saved.parts);
  return (
    <section className="rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200">
      <h3 className="font-semibold text-emerald-900">✓ {saved.pieces.length > 1 ? t("stock.savedN", { n: saved.pieces.length }) : t("stock.saved")}</h3>
      <p className="mt-1 font-mono text-lg font-bold text-slate-900">
        {saved.pieces.map((p) => `${p.reference}${p.unit_count && p.unit_count > 1 ? ` (${p.unit_number}/${p.unit_count})` : ""}`).join(" · ")}
      </p>
      {first && <p className="text-sm text-slate-700">{[productName(first), first.product?.sku, t(`stock.cond.${first.condition}`),
        [first.pallet && t("stock.palletN", { p: first.pallet }), first.rack].filter(Boolean).join(" · ")].filter(Boolean).join(" · ")}</p>}
      {saved.parts > 1 && <p className="mt-1 text-xs text-emerald-900">{t("stock.wrapEach", { n: saved.parts })}</p>}
      {saved.photosFailed > 0 && <p className="mt-1 text-xs text-red-700">{t("stock.photosFailed", { n: saved.photosFailed })}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={onLabels} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white">⬇ {labels > 1 ? t("label.dlAll", { n: labels }) : t("label.dlOne")}</button>
        {onNextSame && <button onClick={onNextSame} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.nextSame")}</button>}
        <button onClick={onNext} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.next")}</button>
      </div>
    </section>
  );
}

function ReturnPicker({ warehouseId, value, autoPickId, onChange }: {
  warehouseId: number; value: ReturnEvent | "none" | null; autoPickId: number | null;
  onChange: (r: ReturnEvent | "none" | null) => void;
}) {
  const { t } = useT();
  const returns = useQuery({ queryKey: ["stock-returns", warehouseId], queryFn: () => fetchReturns(warehouseId) });
  const expectedOf = useExpected(value && value !== "none" ? [value.id] : [], !!value && value !== "none");
  useEffect(() => {
    const hit = autoPickId && !value ? returns.data?.returns.find((r) => r.id === autoPickId) : undefined;
    if (hit) onChange(hit);
  }, [autoPickId, returns.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once chosen, the list folds away so the return's furniture is right there — not 26 cards down.
  if (value) {
    const current = value === "none" ? null : returns.data?.returns.find((r) => r.id === value.id) ?? value;
    return (
      <div className="flex items-center gap-3 rounded-lg bg-orange-50 px-3 py-2 ring-1 ring-orange-300">
        <span className="min-w-0 flex-1">
          {current ? (
            <>
              <span className="flex items-baseline gap-2">
                <span className="truncate font-medium text-slate-900">{current.client_name || current.contract_name || current.title}</span>
                <span className="text-xs text-slate-500">{shortDay(current.starts_at)}</span>
              </span>
              <ReturnMeta r={current} expected={expectedOf(current.id)} />
            </>
          ) : <span className="text-sm font-medium text-slate-900">{t("stock.noReturn")}</span>}
        </span>
        <button onClick={() => onChange(null)} className="text-sm underline">{t("com.change")}</button>
      </div>
    );
  }

  if (returns.isLoading) return <p className="text-sm text-slate-400">…</p>;
  if (returns.error) return <p className="text-sm text-red-600">{(returns.error as Error).message}</p>;
  const list = returns.data?.returns ?? [];
  return (
    <div className="space-y-3">
      {list.length === 0 && <p className="text-sm text-slate-400">{t("stock.noReturns")}</p>}
      <ReturnList returns={list} onPick={onChange} />
      <button onClick={() => onChange("none")} className="rounded-lg bg-white px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-200">
        {t("stock.noReturn")}
      </button>
    </div>
  );
}

function ReturnFurniture({ eventId, selected, onPick }: { eventId: number; selected: number | null; onPick: (p: Pick) => void }) {
  const { t } = useT();
  const items = useQuery({ queryKey: ["stock-return", eventId], queryFn: () => fetchReturnItems(eventId) });
  if (items.isLoading) return <p className="text-sm text-slate-400">…</p>;
  if (items.error) return <p className="text-sm text-red-600">{(items.error as Error).message}</p>;
  const rows = items.data?.items ?? [];
  if (rows.length === 0) return <p className="mb-3 text-sm text-slate-500">{t("stock.noItems")}</p>;
  return <div className="mb-4"><ReturnGrid items={rows} selected={selected} onPick={(i) => onPick(pickOf(i))} /></div>;
}

const pickOf = (i: ReturnItem): Pick => ({ productId: i.product_id, name: i.name, sku: i.sku, remaining: Math.max(0, i.expected - i.entered) });

// His return grid (retourGrid): grouped by room, unfinished first in each, the catalogue picture,
// "✓ got/expected", the name with its parts badge, Myotaku ref · supplier ref.
function ReturnGrid({ items, selected, onPick }: { items: ReturnItem[]; selected: number | null; onPick: (i: ReturnItem) => void }) {
  const groups = new Map<string, ReturnItem[]>();
  for (const i of items) { const room = i.rooms[0] ?? "—"; groups.set(room, [...(groups.get(room) ?? []), i]); }
  return (
    <div className="space-y-3">
      {[...groups].map(([room, list]) => (
        <div key={room}>
          {groups.size > 1 && <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{room}</p>}
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {list.slice().sort((a, b) => Number(a.entered >= a.expected) - Number(b.entered >= b.expected)).map((i) => {
              const done = i.entered >= i.expected;
              return (
                <li key={i.product_id}>
                  <button onClick={() => onPick(i)}
                    className={`flex h-full w-full flex-col rounded-lg p-2 text-left ring-1 ${selected === i.product_id ? "bg-orange-50 ring-orange-400" : done ? "bg-slate-50 opacity-60 ring-slate-200" : "bg-white ring-slate-200 hover:bg-slate-50"}`}>
                    <span className="relative mb-2 block">
                      {i.image_url ? <img src={i.image_url} alt="" className="h-20 w-full rounded object-contain" /> : <span className="block h-20 w-full rounded bg-slate-100" />}
                      <span className={`absolute right-1 top-1 rounded px-1 text-xs font-semibold tabular-nums ${done ? "bg-emerald-600 text-white" : "bg-white/90 text-slate-800 ring-1 ring-slate-200"}`}>{done ? "✓ " : ""}{i.entered}/{i.expected}</span>
                    </span>
                    <span className="line-clamp-2 text-sm font-medium text-slate-900">{i.name}
                      <CompositionBadge productId={i.product_id} productName={i.name} parts={i.parts ?? 1} suggested={i.parts_suggested} /></span>
                    {i.type_name && <span className="block text-xs text-slate-500">{i.type_name}</span>}
                    <span className="mt-auto pt-1 font-mono text-[11px] text-slate-500">#{i.product_id}{i.sku ? ` · ${i.sku}` : ""}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// His manual search (manualResults): one live box — the return's own items that match, then, from two
// letters, the whole catalogue (the feed's search, a moment after the typing stops).
function CatalogueSearch({ onPick, returnItems = [] }: { onPick: (p: Pick) => void; returnItems?: ReturnItem[] }) {
  const { t } = useT();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(text.trim()), 350);
    return () => window.clearTimeout(id);
  }, [text]);
  const found = useQuery({ queryKey: ["stock-catalogue", query], queryFn: () => searchCatalogue(query), enabled: query.length >= 2 });
  const q = norm(text.trim());
  const fromReturn = q ? returnItems.filter((i) => norm(`${i.name ?? ""} ${i.sku ?? ""} ${i.product_id} ${i.product_type_slug ?? ""} ${i.rooms.join(" ")}`).includes(q)) : [];
  const seen = new Set(fromReturn.map((i) => i.product_id));

  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <form onSubmit={(e) => { e.preventDefault(); setQuery(text.trim()); }} className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("stock.searchPh")}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <button className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.search")}</button>
      </form>
      {fromReturn.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-200">
          {fromReturn.map((i) => (
            <li key={`r${i.product_id}`}>
              <button onClick={() => onPick(pickOf(i))} className="flex w-full items-center gap-3 py-2 text-left hover:bg-white">
                {i.image_url ? <img src={i.image_url} alt="" className="h-10 w-10 rounded object-contain" /> : <span className="h-10 w-10 rounded bg-slate-200" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-slate-900">{i.name}</span>
                  <span className="block truncate text-xs text-slate-500">{[String(i.product_id), i.sku, i.rooms.join(" · "), t("ident.fromReturn")].filter(Boolean).join(" · ")}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {found.data?.unavailable && <p className="mt-2 text-xs text-amber-700">{t("stock.searchUnavailable")}</p>}
      {found.data && !found.data.unavailable && found.data.products.length === 0 && <p className="mt-2 text-xs text-slate-500">{t("stock.searchNone")}</p>}
      <ul className="mt-2 divide-y divide-slate-200">
        {found.data?.products.filter((p) => !seen.has(p.id)).map((p) => (
          <li key={p.id}>
            <button onClick={() => onPick({ productId: p.id, name: p.name, sku: p.sku, remaining: 1 })}
              className="flex w-full items-center gap-3 py-2 text-left hover:bg-white">
              {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 rounded object-contain" /> : <span className="h-10 w-10 rounded bg-slate-200" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-slate-900">{p.name}</span>
                <span className="block truncate text-xs text-slate-500">{[p.sku, p.type?.name, realSize(p.size)].filter(Boolean).join(" · ")}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- inventory ---------------------------------------------------------------------------------

// His inventory (`renderInv`): seven figures, his filters (search, category, pallet, condition, origin,
// status, all warehouses, oldest first), the list grouped by category, and the CSV of what is shown.
function Inventory({ warehouseId, onLabels }: { warehouseId: number | null; onLabels: (p: StockPiece[]) => void }) {
  const { t } = useT();
  const { dateTime } = useFormat();
  const [allWh, setAllWh] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [pal, setPal] = useState("all");
  const [cond, setCond] = useState<"all" | Condition>("all");
  const [src, setSrc] = useState<"all" | "return" | "reception">("all");
  const [status, setStatus] = useState<"stock" | "out" | "all">("stock");
  const [old, setOld] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [exported, setExported] = useState(false);
  const wide = allWh || warehouseId == null;
  // His live view: another phone's new pieces appear without anyone reloading.
  const pieces = useQuery({ queryKey: ["stock-pieces", wide ? null : warehouseId, "all"], queryFn: () => fetchPieces(wide ? null : warehouseId, "all"), refetchInterval: 15000 });

  const base = pieces.data?.pieces ?? [];
  const inS = base.filter((p) => p.status !== "left");
  // His "Today's labels": every piece that came in today at this warehouse and is still here, in
  // the order it came in.
  const todayKey = new Date().toLocaleDateString("sv-SE");
  const today = inS.filter((p) => (warehouseId == null || p.myotaku_warehouse_id === warehouseId)
      && new Date(p.received_at).toLocaleDateString("sv-SE") === todayKey)
    .sort((a, b) => a.received_at.localeCompare(b.received_at));
  const catOf = (p: StockPiece) => p.product?.type?.name || t("stock.otherCat");
  const count = (c: Condition) => inS.filter((p) => p.condition === c).length;
  const age = inS.length ? Math.round(inS.reduce((a, p) => a + p.days_in_stock, 0) / inS.length) : null;
  const pallets = [...inS.reduce((m, p) => (p.pallet ? m.set(p.pallet, (m.get(p.pallet) ?? 0) + 1) : m), new Map<string, number>())]
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  const cats = [...base.filter((p) => p.status !== "left" || status !== "stock")
    .reduce((m, p) => m.set(catOf(p), (m.get(catOf(p)) ?? 0) + 1), new Map<string, number>())].sort((a, b) => a[0].localeCompare(b[0]));

  const rows = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    const list = base.filter((p) => {
      if (status === "stock" && p.status === "left") return false;
      if (status === "out" && p.status !== "left") return false;
      if (cond !== "all" && p.condition !== cond) return false;
      if (src !== "all" && p.source !== src) return false;
      if (cat !== "all" && catOf(p) !== cat) return false;
      if (pal !== "all" && (p.pallet ?? "") !== pal) return false;
      if (words.length) {
        const hay = norm([p.reference, p.product?.name, p.myotaku_product_id, p.product?.sku, p.reserved_for?.client,
          p.pallet && `palette ${p.pallet} pal ${p.pallet}`, p.rack && `rack ${p.rack}`, p.product?.type?.name,
          p.unit_count && p.unit_count > 1 ? `${p.unit_number}/${p.unit_count}` : ""].filter(Boolean).join(" "));
        if (!words.every((w) => hay.includes(w))) return false;
      }
      return true;
    });
    return old ? list.slice().sort((a, b) => b.days_in_stock - a.days_in_stock) : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, q, status, cond, src, cat, pal, old]);
  const groups = [...rows.reduce((m, p) => m.set(catOf(p), [...(m.get(catOf(p)) ?? []), p]), new Map<string, StockPiece[]>())]
    .sort((a, b) => a[0].localeCompare(b[0]));

  const exportCsv = () => {
    downloadCsv(t, rows.slice().sort((a, b) => catOf(a).localeCompare(catOf(b))), catOf,
      `${t("csv.file")}-${wide ? t("csv.fileAll") : warehouseId}-${new Date().toISOString().slice(0, 10)}.csv`);
    setExported(true);
  };
  const chip = (on: boolean) => `rounded-full px-2.5 py-1 text-xs ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`;

  return (
    <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="grid grid-cols-4 gap-2 border-b border-slate-100 px-4 py-3 sm:grid-cols-7">
        {[[inS.length, t("stock.inStockN")], [count("new"), t("stock.cond.new")], [count("good"), t("stock.cond.good")],
          [count("light_marks"), t("stock.cond.light_marks")], [count("damaged"), t("stock.cond.damaged")],
          [base.length - inS.length, t("stock.outN")], [age == null ? "–" : `${age} ${t("stock.days")}`, t("stock.avgAge")]]
          .map(([v, l]) => (
            <div key={String(l)} className="rounded-lg bg-slate-50 px-2 py-1.5 text-center">
              <div className="text-lg font-semibold tabular-nums text-slate-900">{v}</div>
              <div className="text-xs text-slate-500">{l}</div>
            </div>
          ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("stock.searchAll")}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="all">{t("stock.allCats")}</option>
          {cats.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
        </select>
        <select value={pal} onChange={(e) => setPal(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="all">{t("stock.allPallets")}</option>
          {pallets.map(([pp, n]) => <option key={pp} value={pp}>{t("stock.palletN").replace("{p}", pp)} ({n})</option>)}
        </select>
        <div className="flex flex-wrap gap-1">
          {(["all", "new", "good", "light_marks", "damaged"] as const).map((c) => (
            <button key={c} onClick={() => setCond(c)} className={chip(cond === c)}>{c === "all" ? t("stock.all") : t(`stock.cond.${c}`)}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {([["all", t("stock.allSources")], ["return", t("stock.srcReturns")], ["reception", t("stock.srcReceptions")]] as const).map(([v, l]) => (
            <button key={v} onClick={() => setSrc(v)} className={chip(src === v)}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {([["stock", t("stock.inStockN")], ["out", t("stock.outN")], ["all", t("stock.all")]] as const).map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} className={chip(status === v)}>{l}</button>
          ))}
        </div>
        <button onClick={() => setAllWh(!allWh)} className={chip(wide)}>{t("stock.allWarehouses")}</button>
        <button onClick={() => setOld(!old)} className={chip(old)}>{t("stock.oldestFirst")}</button>
        <button onClick={exportCsv} disabled={rows.length === 0} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300 disabled:opacity-40">{t("stock.exportCsv")}</button>
        <button onClick={() => onLabels(today)} disabled={today.length === 0} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300 disabled:opacity-40">🏷 {t("stock.todayLabels")}</button>
        {exported && <span className="text-xs text-emerald-700">✅ {t("stock.csvDone")}</span>}
      </div>
      {pieces.error && <p className="px-4 py-3 text-sm text-red-600">{(pieces.error as Error).message}</p>}
      {!pieces.isLoading && base.length === 0 && (
        <div className="px-4 py-6 text-sm text-slate-500"><b className="block text-slate-700">{t("stock.emptyInvTitle")}</b>{t("stock.emptyInventory")}</div>
      )}
      {base.length > 0 && rows.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">{t("stock.noResults")}</p>}
      {groups.map(([c, list]) => (
        <div key={c}>
          <div className="flex justify-between border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <span>{c}</span><span className="font-mono">{list.length}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {list.slice(0, 200).map((p) => (
              <li key={p.id}>
                <button onClick={() => setOpen(open === p.id ? null : p.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50">
                  {/* His row: the piece's own identification photo, else the catalogue picture. */}
                  {p.photo?.thumb_url || p.photo?.url ? <img src={(p.photo.thumb_url || p.photo.url)!} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
                    : p.product?.image_url ? <img src={p.product.image_url} alt="" className="h-10 w-10 shrink-0 rounded object-contain" /> : <span className="h-10 w-10 shrink-0 rounded bg-slate-100" />}
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-sm text-slate-900">{p.reference}</span>
                    <span className="block truncate text-sm text-slate-700">{productName(p)}
                      {p.unit_count && p.unit_count > 1 ? <span className="ml-1 rounded bg-slate-900 px-1.5 text-xs text-white">{p.unit_number}/{p.unit_count}</span> : null}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {[p.myotaku_product_id && `#${p.myotaku_product_id}`, p.product?.sku].filter(Boolean).join(" · ")}
                      {p.reserved_for?.client && <b className="ml-1 text-amber-800">· {t("stock.forClient")} {p.reserved_for.client}</b>}
                    </span>
                  </span>
                  <span className="flex flex-col items-end gap-0.5 text-xs text-slate-500">
                    {p.status === "left"
                      ? <span className="rounded bg-slate-200 px-2 py-0.5 text-slate-700">{t("stock.status.left")}</span>
                      : <span className={`rounded px-2 py-0.5 ${conditionTone(p.condition, false)}`}>{t(`stock.cond.${p.condition}`)}</span>}
                    <span>{dateTime(p.received_at).split(" ")[0]}{wide && p.warehouse_name ? ` · ${p.warehouse_name}` : ""}</span>
                    <span>{[p.pallet && `${t("stock.palletShort")} ${p.pallet}`, p.rack].filter(Boolean).join(" · ")}</span>
                    {/* His "over three months: offer it first" — the age turns red past 90 days. */}
                    {p.status !== "left" && <span className={p.days_in_stock > 90 ? "font-semibold text-red-700" : ""}>{p.days_in_stock} {t("stock.days")}</span>}
                  </span>
                </button>
                {open === p.id && <PieceDetail id={p.id} onLabels={onLabels} onGone={() => setOpen(null)} />}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// His accents-off, lower-case search text.
const norm = (s: unknown) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// His `exportCsv`: his 24 columns, ";" separated, UTF-8 with BOM, sorted by category.
// His CSV: French column names and values exactly as his file (only the age column follows the
// language there); the other three languages get the same columns in their own words.
const CSV_COLS = ["stock_no", "entrepot", "categorie", "statut", "etat", "origine", "nom", "ref_myotaku", "ref_fournisseur", "fournisseur",
  "commande_fournisseur", "dimensions", "emplacement", "palette", "rack", "numero", "jours_au_depot", "retour", "client", "reserve_pour",
  "livraison", "entree_le", "degats", "catalogue_url"];
function downloadCsv(t: T, rows: StockPiece[], catOf: (p: StockPiece) => string, filename: string) {
  const cols = CSV_COLS.map((c) => t(`csv.col.${c}`));
  const cell = (v: unknown) => { const x = String(v ?? ""); return /[",;\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
  const lines = [cols.join(";")].concat(rows.map((p) => [p.reference, p.warehouse_name, catOf(p), t(p.status === "left" ? "csv.out" : "csv.in"),
    t(`csv.cond.${p.condition}`), t(p.source === "reception" ? "csv.reception" : "csv.return"), p.product?.name, p.myotaku_product_id, p.product?.sku, "",
    "", realSize(p.product?.size), [p.pallet && t("csv.pallet", { p: p.pallet }), p.rack && t("csv.rack", { r: p.rack })].filter(Boolean).join(" · "), p.pallet, p.rack,
    p.unit_count && p.unit_count > 1 ? `${p.unit_number}/${p.unit_count}` : "", p.status === "left" ? "" : p.days_in_stock,
    p.myotaku_return_event_id ? `#${p.myotaku_return_event_id}` : "", "", p.reserved_for?.client, p.reserved_for?.delivery_on,
    p.received_at.slice(0, 16).replace("T", " "), p.damage_note, p.product?.public_url].map(cell).join(";")));
  const url = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// His statistics cards (statsHtml): the current stay or the exit, the total across stays, ins and
// outs with their reasons, the last movement, the time at this place, the same reference.
function PieceStatsCards({ s }: { s: PieceStats }) {
  const { t } = useT();
  const d = (n: number) => t(n === 1 ? "stock.day1" : "stock.daysN", { n });
  const reasons = Object.entries(s.outs).map(([r, n]) => `${n} × ${t(`stock.out.${r}`)}`).join(" · ") || "—";
  const cards: [string, string, string, boolean?][] = [
    s.in_stock ? [t("stock.stIn"), d(s.stay_days), t(s.back ? "stock.stBackSince" : "stock.stInSince", { date: shortDay(s.stay_since) }), s.stay_days > 90]
      : [t("stock.stOut"), shortDay(s.out_on), t("stock.stOutAfter", { n: d(s.stay_days) })],
    [t("stock.stTotal"), d(s.total_days), t("stock.stTotalH")],
    [t("stock.stCycles"), t("stock.stCyclesV", { i: s.ins, o: Object.values(s.outs).reduce((a, n) => a + n, 0) }), reasons],
  ];
  if (s.last_movement) cards.push([t("stock.stLast"), shortDay(s.last_movement.at), t(`stock.ev.${s.last_movement.action}`)]);
  if (s.in_stock && s.place_since) cards.push([t("stock.stPlace"), d(Math.floor((Date.now() - Date.parse(s.place_since)) / 864e5)), t("stock.stPlaceSince", { date: shortDay(s.place_since) })]);
  cards.push([t("stock.stRef"), t("stock.stRefV", { n: s.same_here }), t("stock.stRefH", { all: s.same_all, out: s.same_out })]);
  cards.push([t("stock.stAvg"), d(s.same_avg_days), t("stock.stAvgH", { n: s.same_count })]);
  cards.push([t("stock.stRet"), String(s.same_from_returns), t("stock.stRetH")]);
  const exits = Object.values(s.outs).reduce((a, n) => a + n, 0);
  return (
    <>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {cards.map(([label, value, hint, alert]) => (
          <div key={label} className={`rounded-lg px-2.5 py-2 ring-1 ${alert ? "bg-red-50 ring-red-200" : "bg-white ring-slate-200"}`}>
            <div className="text-[11px] text-slate-500">{label}</div>
            <div className={`text-base font-semibold ${alert ? "text-red-700" : "text-slate-900"}`}>{value}</div>
            <div className="text-[11px] leading-tight text-slate-500">{hint}</div>
          </div>
        ))}
      </div>
      {s.stays && s.stays.length > 0 && (exits > 0 || s.stays.length > 1) && <StaysBar stays={s.stays} />}
    </>
  );
}

// His timeline bar (statsHtml `tl`): from the first entry to today, in stock and out, with its key.
function StaysBar({ stays }: { stays: [string, string][] }) {
  const { t } = useT();
  const t0 = Date.parse(stays[0][0]), t1 = Date.now(), w = Math.max(1, t1 - t0);
  const seg: ["in" | "out", number][] = [];
  let cur = t0;
  for (const [a, b] of stays) {
    const from = Date.parse(a), to = Date.parse(b);
    if (from > cur) seg.push(["out", from - cur]);
    seg.push(["in", Math.max(0, to - from)]);
    cur = Math.max(cur, to);
  }
  if (cur < t1) seg.push(["out", t1 - cur]);
  const colour = { in: "bg-emerald-500", out: "bg-slate-300" };
  return (
    <div className="mt-2.5 flex flex-col gap-1">
      <div role="img" aria-label={`${t("stock.tlIn")} / ${t("stock.tlOut")}`} className="flex h-3 overflow-hidden rounded-md bg-slate-100">
        {seg.map(([k, n], i) => <i key={i} className={`block h-full ${colour[k]}`} style={{ width: `${((100 * n) / w).toFixed(2)}%` }} />)}
      </div>
      <div className="flex justify-between gap-2 font-mono text-[11px] text-slate-500"><span>{shortDay(stays[0][0])}</span><span>{t("stock.today")}</span></div>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-slate-500">
        {(["in", "out"] as const).map((k) => (
          <span key={k}><i className={`mr-1 inline-block h-2.5 w-2.5 rounded-sm align-[-1px] ${colour[k]}`} />{t(k === "in" ? "stock.tlIn" : "stock.tlOut")}</span>
        ))}
      </div>
    </div>
  );
}

function PieceDetail({ id, onLabels, onGone }: { id: number; onLabels: (p: StockPiece[]) => void; onGone: () => void }) {
  const { t } = useT();
  const { staff } = useAuth();
  const qc = useQueryClient();
  const piece = useQuery({ queryKey: ["stock-piece", id], queryFn: () => fetchPiece(id) });
  const [pallet, setPallet] = useState("");
  const [rack, setRack] = useState("");
  useEffect(() => { setPallet(piece.data?.pallet || ""); setRack(piece.data?.rack || ""); }, [piece.data?.pallet, piece.data?.rack]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["stock-piece", id] }); void qc.invalidateQueries({ queryKey: ["stock-pieces"] }); setMode(null); };
  const [mode, setMode] = useState<null | "cond" | "out" | "del">(null);
  const [reason, setReason] = useState<string>(OUT_REASONS[0]);
  const [note, setNote] = useState("");
  const move = useMutation({ mutationFn: () => updatePiece(id, { pallet, rack }), onSuccess: refresh });
  const grade = useMutation({ mutationFn: (c: Condition) => updatePiece(id, { condition: c, damage_note: c === "good" || c === "new" ? null : note.trim() || null }), onSuccess: refresh });
  const leave = useMutation({ mutationFn: () => leavePiece(id, reason), onSuccess: refresh });
  const back = useMutation({ mutationFn: () => backPiece(id), onSuccess: refresh });
  const del = useMutation({ mutationFn: () => deletePiece(id), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["stock-pieces"] }); onGone(); } });
  const failed = (grade.error || leave.error || back.error || del.error || move.error) as Error | null;
  const { dateTime } = useFormat();

  if (!piece.data) return <p className="px-4 pb-3 text-sm text-slate-400">…</p>;
  const p = piece.data;
  const yes = (b: boolean) => (b ? "✓" : "✗");
  const fact = (label: string, value: React.ReactNode) => <><dt className="text-slate-500">{label}</dt><dd className="text-slate-900">{value}</dd></>;
  return (
    <div className="grid gap-4 bg-slate-50 px-4 py-3 md:grid-cols-2">
      <div className="space-y-3 text-sm text-slate-700">
        {/* His sheet header: Out, the condition, the warehouse. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {p.status === "left" && <span className="rounded bg-slate-700 px-2 py-0.5 text-xs font-semibold text-white">{t("stock.outPill")}</span>}
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${conditionTone(p.condition, true)}`}>{t(`stock.cond.${p.condition}`)}</span>
          {p.warehouse_name && <span className="rounded bg-white px-2 py-0.5 text-xs text-slate-600 ring-1 ring-slate-200">{p.warehouse_name}</span>}
        </div>
        {p.stats && <PieceStatsCards s={p.stats} />}
        <ReadOnlyArea module="stock">
          <MediaStrip type="stock_piece" id={p.id} extra={p.product?.image_url ? { url: p.product.image_url, caption: t("stock.catPhoto") } : null} />
        </ReadOnlyArea>
        {/* His facts. */}
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
          {fact(t("label.refMyo"), <span className="font-mono">{p.myotaku_product_id ?? "—"}</span>)}
          {fact(t("label.refSup"), <span className="font-mono">{p.product?.sku || "—"}</span>)}
          {p.reserved_for?.client && fact(t("stock.forClient"), <><b>{p.reserved_for.client}</b>{p.reserved_for.delivery_on ? ` · ${t("label.deliv")} ${shortDay(p.reserved_for.delivery_on)}` : ""}{p.reserved_for.owner ? ` · ${p.reserved_for.owner}` : ""}</>)}
          {fact(t("stock.typeSize"), [p.product?.type?.name, realSize(p.product?.size)].filter(Boolean).join(" · ") || "—")}
          {fact(t("stock.composition"), p.piece_names.length > 1 ? <><b>{t("stock.nParts", { n: p.piece_names.length })}</b> · {p.piece_names.join(" · ")}</> : t("compo.single"))}
          {p.unit_count && p.unit_count > 1 ? fact(t("stock.itemNo"), <><b>{p.unit_number}/{p.unit_count}</b> {/* His suffix: "of the return" / "of this intake", none for a piece set aside for a client. */}
            {!p.reserved_for && <span className="text-slate-500">{t(p.myotaku_return_event_id ? "stock.ofReturn" : "stock.ofIntake")}</span>}</>) : null}
          {fact(t("stock.place"), [p.pallet && t("stock.palletN", { p: p.pallet }), p.rack].filter(Boolean).join(" · ") || "—")}
          {p.myotaku_return_event_id && fact(t("stock.factReturn"), [`#${p.myotaku_return_event_id}`, p.return_snapshot?.client, p.return_snapshot?.title,
            p.return_snapshot?.type, p.return_snapshot?.date ? shortDay(p.return_snapshot.date) : null].filter(Boolean).join(" · "))}
          {p.product_snapshot?.supplier && fact(t("ident.supplier"), p.product_snapshot.supplier)}
          {/* His detail: the piece's own supplier order when it has one, else the orders carrying its product. */}
          {p.myotaku_order_id ? fact(t("stock.supplierOrder"), [<span key="o" className="font-mono">#{p.myotaku_order_id}</span>, p.product_snapshot?.supplier,
            p.product_snapshot?.arrival_on && shortDay(p.product_snapshot.arrival_on)].filter(Boolean).flatMap((x, i) => (i ? [" · ", x] : [x])))
            : p.status !== "left" && p.myotaku_product_id && <div className="col-span-2"><OrderHint productId={p.myotaku_product_id} sku={p.product?.sku} warehouseId={p.myotaku_warehouse_id} /></div>}
          {p.damage_note && fact(t("stock.damageNote"), <span className="text-red-700">{p.damage_note}</span>)}
          {fact(t("stock.factWrapping"), `${yes(p.wrapped_in_blanket)} ${t("stock.blanket")} · ${yes(p.wrapped_in_film)} ${t("stock.film")}`)}
          {fact(t("stock.factAdded"), `${dateTime(p.received_at)}${p.received_by ? ` · ${p.received_by}` : ""}`)}
        </dl>
        {p.product?.public_url && (
          <a href={p.product.public_url} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-sky-700 underline">{t("stock.openCatalogue")}</a>
        )}
        <ReadOnlyArea module="stock">
        <div className="space-y-3">
        <div className="flex items-end gap-2">
          <Field label={t("stock.pallet")} value={pallet} onChange={setPallet} />
          <Field label={t("stock.rack")} value={rack} onChange={setRack} />
          <button onClick={() => move.mutate()} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.move")}</button>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={() => onLabels([p])} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.reprint")}</button>
          <button onClick={() => setMode(mode === "cond" ? null : "cond")} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.changeCond")}</button>
          {p.status === "left"
            ? <button onClick={() => back.mutate()} className="rounded-lg bg-white px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.backIn")}</button>
            : p.status === "in_stock" && <button onClick={() => setMode(mode === "out" ? null : "out")} className="rounded-lg px-3 py-1.5 text-sm text-red-700 ring-1 ring-red-200">{t("stock.markOut")}</button>}
          {staff?.is_admin && <button onClick={() => setMode(mode === "del" ? null : "del")} className="rounded-lg px-3 py-1.5 text-sm text-slate-500 underline">{t("stock.delete")}</button>}
        </div>
        {mode === "cond" && (
          <div className="flex flex-wrap items-center gap-2">
            {(["good", "light_marks", "damaged"] as const).map((c) => (
              <button key={c} onClick={() => grade.mutate(c)}
                className={`rounded-lg px-3 py-1.5 text-sm ring-1 ${conditionTone(c, p.condition === c)}`}>{t(`stock.cond.${c}`)}</button>
            ))}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("stock.damageNotePh")}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
        )}
        {mode === "out" && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm">{t("stock.reason")}{" "}
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm">
                {OUT_REASONS.map((r) => <option key={r} value={r}>{t(`stock.out.${r}`)}</option>)}
              </select>
            </label>
            <button onClick={() => leave.mutate()} className="rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-800 ring-1 ring-red-300">{t("stock.markOut")}</button>
          </div>
        )}
        {mode === "del" && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
            <span>{t("stock.deleteConfirm")}</span>
            <button onClick={() => del.mutate()} className="rounded bg-red-700 px-3 py-1 text-white">{t("stock.deleteYes")}</button>
            <button onClick={() => setMode(null)} className="underline">{t("unit.no")}</button>
          </div>
        )}
        </div>
        </ReadOnlyArea>
        {failed && <p className="text-sm text-red-700">{failed.message}</p>}
      </div>
      {/* His history: newest first. */}
      <ol className="space-y-1 text-xs text-slate-600">
        {p.events?.slice().reverse().map((e, i) => (
          <li key={i}><span className="text-slate-400">{dateTime(e.at)}</span> · {t(`stock.ev.${e.action}`)}
            {e.value && <span className="text-slate-500"> — {eventValue(e.action, e.value, t)}</span>}
            {e.by_name && <span className="text-slate-400"> · {e.by_name}</span>}</li>
        ))}
      </ol>
    </div>
  );
}

// ---- labels: one per piece, and one per part when the product comes in several -----------------

// His labels: a PDF in the format chosen for this warehouse's printer, with a QR code to the
// catalogue page on each one — one label per piece of a product in several pieces. The preview is
// drawn from the same layout as the PDF, so what is shown is what prints.
const FORMAT_KEY = "myotaku-warehouse:label-format";
function readFormat(): FormatKey {
  try { const f = localStorage.getItem(FORMAT_KEY); if (f && f in FORMATS) return f as FormatKey; } catch { /* storage blocked */ }
  return "a4";
}
// His label format: chosen once, remembered on this device, the same in the intake and the label sheet.
function useLabelFormat() {
  const [format, setFormat] = useState<FormatKey>(readFormat);
  const choose = (f: FormatKey) => { setFormat(f); try { localStorage.setItem(FORMAT_KEY, f); } catch { /* this tab only */ } };
  return [format, choose] as const;
}
// The label engine (with its PDF library) loads only when a label is shown.
function useLabelEngine() {
  const [lib, setLib] = useState<typeof import("@/lib/stockLabels") | null>(null);
  useEffect(() => { void import("@/lib/stockLabels").then(setLib); }, []);
  return lib;
}

function FormatPicker({ format, onChange }: { format: FormatKey; onChange: (f: FormatKey) => void }) {
  const { t } = useT();
  return (
    <label className="mb-3 block text-xs text-slate-500">{t("label.format")}
      <select value={format} onChange={(e) => onChange(e.target.value as FormatKey)}
        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900">
        {(Object.keys(FORMATS) as FormatKey[]).map((f) => <option key={f} value={f}>{t(`label.fmt.${f}`)}</option>)}
      </select>
      <span className="mt-1 block">{t(FORMATS[format].big ? "label.printA4" : "label.printRoll")}</span>
    </label>
  );
}

// His step 5: the label as it will print, before saving — "EXAMPLE" until a product is chosen.
function IntakeLabel({ pick, condition, ret }: { pick: Pick | null; condition: Condition; ret: ReturnEvent | "none" | null }) {
  const { t, locale } = useT();
  const [format, choose] = useLabelFormat();
  const lib = useLabelEngine();
  const compo = useQuery({ queryKey: ["composition", pick?.productId], queryFn: () => fetchComposition(pick!.productId!), enabled: !!pick?.productId });
  const parts = compo.data?.piece_names ?? [];
  const svg = useMemo(() => {
    if (!lib) return "";
    const day = new Date().toISOString().slice(2, 10).replace(/-/g, "");
    const rec = pick ? lib.draftRecord({
      reference: `P${day}-··`, name: pick.name, productId: pick.productId, sku: pick.sku, condition,
      parts, returnEventId: ret && ret !== "none" ? ret.id : null,
    }) : lib.SAMPLE;
    return lib.labelSvg(rec, format, locale);
  }, [lib, pick, condition, ret, parts, format, locale]);
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <p className="mb-2 text-sm font-medium text-slate-700">{t("label.s5")}</p>
      {svg && <div className="mb-2 rounded-lg bg-slate-100 p-2" aria-label={t("label.preview")} dangerouslySetInnerHTML={{ __html: svg }} />}
      {parts.length > 1 && <p className="mb-2 text-xs text-slate-600"><b>{t("label.count", { n: parts.length })}</b> · {t("label.previewFirst")}</p>}
      <FormatPicker format={format} onChange={choose} />
    </div>
  );
}

function LabelSheet({ pieces, onClose, forClient = null, autoDownload = false }: {
  pieces: StockPiece[]; onClose: () => void; forClient?: { client: string | null; date: string | null } | null; autoDownload?: boolean;
}) {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const [format, choose] = useLabelFormat();
  const [busy, setBusy] = useState(false);
  const lib = useLabelEngine();
  const recs = useMemo(() => lib?.recordsFor(pieces, forClient) ?? [], [lib, pieces, forClient]);
  const preview = useMemo(() => (lib && recs[0] ? lib.labelSvg(recs[0], format, locale) : ""), [lib, recs, format, locale]);
  const download = async () => {
    setBusy(true);
    try {
      const day = new Date().toISOString().slice(2, 10).replace(/-/g, "");
      if (!lib) return;
      lib.saveBlob(lib.labelsPdf(recs, format, locale),
        pieces.length === 1 ? t("label.file1", { id: pieces[0].reference }) : t("label.fileN", { wh: pieces[0].myotaku_warehouse_id, day }));
      await Promise.all(pieces.map((p) => labelPiece(p.id).catch(() => null)));
      void qc.invalidateQueries({ queryKey: ["stock-pieces"] });
    } finally { setBusy(false); }
  };
  // Once, as soon as the label engine is loaded: the sheet stays open to download again in another format.
  const auto = useRef(autoDownload);
  useEffect(() => {
    if (auto.current && lib && recs.length) { auto.current = false; void download(); }
  }, [lib, recs]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="font-medium text-slate-900">{t("stock.labelsReady", { n: recs.length })}</p>
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("stock.close")}</button>
        </div>
        <FormatPicker format={format} onChange={choose} />
        {preview && (
          <div className="mx-auto mb-3 max-w-md rounded-lg bg-slate-100 p-2" aria-label={t("label.preview")}
            dangerouslySetInnerHTML={{ __html: preview }} />
        )}
        <button onClick={() => void download()} disabled={busy || !recs.length}
          className="w-full rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
          ⬇ {recs.length > 1 ? t("label.dlAll", { n: recs.length }) : t("label.dlOne")}
        </button>
      </div>
    </div>
  );
}

// ---- small pieces ------------------------------------------------------------------------------

function Step({ n, title, hint, children }: { n: number; title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <h3 className="flex items-center gap-2 font-medium text-slate-900">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-900 text-xs text-white">{n}</span>{title}
      </h3>
      <p className="mb-3 mt-1 text-sm text-slate-500">{hint}</p>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="min-w-0 text-xs text-slate-500">{label}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" />
    </label>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="rounded-xl bg-white px-5 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">{text}</p>;
}

function conditionTone(c: Condition, strong: boolean) {
  if (c === "damaged") return strong ? "bg-red-50 text-red-800 ring-red-400" : "bg-red-50 text-red-700";
  if (c === "light_marks") return strong ? "bg-amber-50 text-amber-800 ring-amber-400" : "bg-amber-50 text-amber-700";
  return strong ? "bg-emerald-50 text-emerald-800 ring-emerald-400" : "bg-emerald-50 text-emerald-700";
}

// Some history values are codes ("return", "person"); they are shown in the reader's language.
function eventValue(action: string, value: string, t: (k: string) => string) {
  if (action === "received") return t(`stock.src.${value}`);
  if (action === "identified") return t(`stock.by.${value}`);
  if (action === "graded") return t(`stock.cond.${value}`);
  if (action === "left" && /^(redeliv|repair|sale|scrap|transfer)$/.test(value)) return t(`stock.out.${value}`);
  return value;
}

// The catalogue writes "0x0x0" when it does not know a size; a label must not print that as a size.
const realSize = (size: string | null | undefined) => (size && !/^[0\s.,x×]*$/i.test(size) ? size : null);

const productName = (p: StockPiece) => p.product?.name || (p.myotaku_product_id ? `#${p.myotaku_product_id}` : "—");


// The pallet and rack are carried over to the next piece: a crew fills one pallet at a time.
function remembered(w: number | null, k: string) {
  try { return (w && localStorage.getItem(`warehouse:stock:${w}:${k}`)) || ""; } catch { return ""; }
}
function remember(w: number | null, k: string, v: string) {
  try { if (w) localStorage.setItem(`warehouse:stock:${w}:${k}`, v); } catch { /* storage unavailable */ }
}

// ---- Retours: per return of the planning, what is expected and what is already in stock ----------

// His Retours tab: the planning's returns for this warehouse — Today, Upcoming (soonest first),
// Recent (latest first) — each with what is already in stock against what is expected; opened, the
// same picture grid as the intake, and a tap starts that piece's intake.
function ReturnsView({ warehouseId, onEnter }: { warehouseId: number | null; onEnter: (returnId: number, pick: Pick) => void }) {
  const { t } = useT();
  const returns = useQuery({ queryKey: ["stock-returns", warehouseId], queryFn: () => fetchReturns(warehouseId) });
  const [open, setOpen] = useState<Set<number>>(new Set());
  const list = returns.data?.returns ?? [];
  const today = new Date().toLocaleDateString("sv-SE");
  const day = (r: ReturnEvent) => (r.starts_at ? new Date(r.starts_at).toLocaleDateString("sv-SE") : "");
  const byDate = (a: ReturnEvent, b: ReturnEvent) => (a.starts_at ?? "").localeCompare(b.starts_at ?? "");
  const groups: [string, ReturnEvent[]][] = [
    ["today", list.filter((r) => day(r) === today).sort(byDate)],
    ["upcoming", list.filter((r) => day(r) > today).sort(byDate)],
    ["recent", list.filter((r) => day(r) < today).sort(byDate).reverse()],
  ];
  const whName = returns.data?.warehouses.find((w) => w.id === warehouseId)?.name;
  const toggle = (id: number) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <section className="max-w-4xl">
      <h2 className="text-lg font-semibold text-slate-900">{t("ret.title")}{whName ? ` · ${whName}` : ""}</h2>
      <p className="mb-2 text-sm text-slate-500">{t("ret.hint")}</p>
      {returns.error && <p className="text-sm text-red-600">{(returns.error as Error).message}</p>}
      {returns.isLoading && <p className="text-sm text-slate-400">…</p>}
      {!returns.isLoading && list.length === 0 && (
        <div className="rounded-xl bg-white px-5 py-6 text-sm text-slate-500 ring-1 ring-slate-200"><b className="block text-slate-700">{t("ret.emptyTitle")}</b>{t("ret.empty")}</div>
      )}
      {groups.map(([k, rows]) => rows.length > 0 && (
        <div key={k} className="mt-4">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{t(`ret.group.${k}`)}</p>
          <div className="space-y-1.5">
            {rows.map((r) => <ReturnRow key={r.id} r={r} open={open.has(r.id)} onToggle={() => toggle(r.id)} onEnter={(i) => onEnter(r.id, pickOf(i))} />)}
          </div>
        </div>
      ))}
    </section>
  );
}

function ReturnRow({ r, open, onToggle, onEnter }: { r: ReturnEvent; open: boolean; onToggle: () => void; onEnter: (i: ReturnItem) => void }) {
  const { t, locale } = useT();
  const items = useQuery({ queryKey: ["stock-return", r.id], queryFn: () => fetchReturnItems(r.id) });
  const expected = items.data?.items.reduce((a, i) => a + i.expected, 0);
  const got = items.data?.items.reduce((a, i) => a + Math.min(i.entered, i.expected), 0) ?? r.entered;
  const pct = expected ? Math.min(100, Math.round((got / expected) * 100)) : 0;
  const date = r.starts_at ? new Date(r.starts_at) : null;
  const tag = { fr: "fr-CH", en: "en-GB", de: "de-CH", it: "it-CH" }[locale];
  return (
    <div className="rounded-xl bg-white ring-1 ring-slate-200">
      <button onClick={onToggle} aria-expanded={open} className="grid w-full grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 text-left">
        <span className="text-xs text-slate-500"><b className="block text-sm text-slate-900">{shortDay(r.starts_at).slice(0, 5)}</b>{date ? date.toLocaleDateString(tag, { weekday: "short" }) : ""}</span>
        <span className="min-w-0">
          <span className="block truncate font-medium text-slate-900">{r.client_name || r.contract_name || r.title}</span>
          <span className="block truncate text-xs text-slate-500">{[r.type_label, r.title].filter(Boolean).join(" · ")}</span>
        </span>
        <span className="w-28 text-right text-xs tabular-nums text-slate-600">
          {got} / {expected ?? "…"}
          <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-slate-100"><i className="block h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} /></span>
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 py-3">
          <p className="mb-2 text-xs text-slate-500">{t("ret.tapToEnter")}</p>
          {items.data?.items.length ? <ReturnGrid items={items.data.items} selected={null} onPick={onEnter} />
            : <p className="text-sm text-slate-400">{items.isLoading ? "…" : t("ret.noItems")}</p>}
        </div>
      )}
    </div>
  );
}

// ---- Étiquetage des réceptions: label each piece ordered for a client, after assembly and packing --

function LabellingView({ warehouseId, onLabels }: {
  warehouseId: number | null; onLabels: (p: StockPiece[], f: { client: string | null; date: string | null }) => void;
}) {
  const { t, locale } = useT();
  const deliveries = useQuery({ queryKey: ["labelling", warehouseId], queryFn: () => fetchLabelling(warehouseId), refetchInterval: 15000 });
  const [open, setOpen] = useState<number | null>(null);
  const list = deliveries.data?.deliveries ?? [];
  const today = todayInZurich();
  // His sections: upcoming (soonest first, "to schedule" last) and past (latest first, kept 7 days).
  const up = list.filter((d) => !d.delivery_on || d.delivery_on >= today).sort((a, b) => (a.delivery_on ?? "9999").localeCompare(b.delivery_on ?? "9999"));
  const past = list.filter((d) => d.delivery_on && d.delivery_on < today).sort((a, b) => (b.delivery_on ?? "").localeCompare(a.delivery_on ?? ""));
  const weekday = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { weekday: "short" }).replace(".", "");

  const card = (d: LabellingDelivery) => (
    <LabellingCard key={d.event_id} d={d} open={open === d.event_id} weekday={weekday}
      onToggle={() => setOpen(open === d.event_id ? null : d.event_id)} onLabels={onLabels} />
  );
  const section = (key: string, rows: LabellingDelivery[]) => rows.length > 0 && (
    <div key={key}>
      <h3 className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t(key)}</h3>
      <ul className="space-y-1.5">{rows.map(card)}</ul>
    </div>
  );
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{t("rec.title")}</h2>
        <p className="text-xs text-slate-500">{t("rec.hint")}</p>
      </div>
      {deliveries.error && <p className="px-4 py-3 text-sm text-red-600">{(deliveries.error as Error).message}</p>}
      {list.length === 0 && !deliveries.isLoading && <p className="px-4 py-6 text-sm text-slate-400">{t("rec.empty")}</p>}
      <div className="px-4 pb-4">
        {section("rec.upcoming", up)}
        {section("rec.past", past)}
      </div>
    </section>
  );
}

// One job's header: date + weekday (or "to schedule"), client, #number · owner, assembly status, and
// his labelled / total with a bar — its numbers fetched on their own, so each card fills in as it comes.
function LabellingCard({ d, open, weekday, onToggle, onLabels }: {
  d: LabellingDelivery; open: boolean; weekday: (iso: string) => string; onToggle: () => void;
  onLabels: (p: StockPiece[], f: { client: string | null; date: string | null }) => void;
}) {
  const { t } = useT();
  const totals = useQuery({ queryKey: ["labelling-totals", d.event_id, d.labelled], queryFn: () => fetchLabellingTotals([d.event_id]), staleTime: 60_000 });
  const tot = totals.data?.totals[String(d.event_id)];
  const labelled = tot?.labelled ?? d.labelled;
  const pct = tot?.total ? Math.round((labelled / tot.total) * 100) : 0;
  return (
    <li className="overflow-hidden rounded-lg ring-1 ring-slate-200">
      <button onClick={onToggle} aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50">
        <span className="w-16 shrink-0 text-center text-xs text-slate-500">
          {d.delivery_on ? <><b className="block text-sm text-slate-900">{shortDay(d.delivery_on)}</b>{weekday(d.delivery_on)}</>
            : <><b className="block text-sm text-slate-900">—</b>{t("rec.toSchedule")}</>}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-900">{d.client_name || d.contract_name}</span>
          <span className="block truncate text-xs text-slate-500">
            {[d.number ? `#${d.number}` : null, d.owner].filter(Boolean).join(" · ")}
            {d.run_status === "finished" ? <b className="text-emerald-700"> · {t("rec.runDone")}</b>
              : d.run_status === "running" || d.run_status === "paused" ? <> · {t("rec.runGoing")}</> : null}
          </span>
        </span>
        <span className="w-24 shrink-0 text-right text-xs tabular-nums text-slate-600">
          {labelled} / {tot?.total ?? "…"}
          <span className="mt-1 block h-1.5 overflow-hidden rounded bg-slate-100"><i className="block h-full bg-emerald-500" style={{ width: `${pct}%` }} /></span>
        </span>
      </button>
      {open && <LabellingUnits eventId={d.event_id} onLabels={onLabels} />}
    </li>
  );
}

function LabellingUnits({ eventId, onLabels }: { eventId: number; onLabels: (p: StockPiece[], f: { client: string | null; date: string | null }) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const units = useQuery({ queryKey: ["labelling-units", eventId], queryFn: () => fetchLabellingUnits(eventId) });
  const forClient = () => ({ client: units.data?.event.client_name ?? null, date: units.data?.event.delivery_on ?? null });
  // His "Réimprimer" on a line: the labels of the pieces already made for it, again.
  const reprint = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => fetchPiece(id))),
    onSuccess: (pieces) => onLabels(pieces, forClient()),
  });
  const label = useMutation({
    mutationFn: (row?: number) => labelReception(eventId, row),
    onSuccess: (r) => {
      ["labelling-units", "labelling", "labelling-totals", "stock-pieces"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
      if (r.pieces.length) onLabels(r.pieces, forClient());
    },
  });
  if (!units.data) return <p className="px-4 pb-3 text-sm text-slate-400">…</p>;
  const lines = new Map<number, LabellingUnit[]>();
  units.data.units.forEach((u) => lines.set(u.row, [...(lines.get(u.row) ?? []), u]));
  const left = units.data.units.filter((u) => !u.piece_id).length;
  // His order: lines still to label first.
  const sorted = [...lines.values()].sort((a, b) => Number(a.every((u) => u.piece_id)) - Number(b.every((u) => u.piece_id)));
  return (
    <div className="space-y-2 bg-slate-50 px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">{t("rec.itemsHint")}</p>
        {left > 0 && (
          <button onClick={() => label.mutate(undefined)} disabled={label.isPending} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
            {label.isPending ? t("rec.labeling") : t("rec.labelAll").replace("{n}", String(left))}
          </button>
        )}
      </div>
      {(label.error || reprint.error) && <p className="text-xs text-red-600">{((label.error || reprint.error) as Error).message}</p>}
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((us) => {
          const done = us.filter((u) => u.piece_id).length;
          const first = us[0];
          const all = done === us.length;
          const sup = first.sku && first.sku.includes("-") && !/^UNKN-\d+$/.test(first.sku) ? first.sku : null;
          return (
            <li key={first.row} className={`flex gap-2.5 rounded-lg bg-white p-2 text-sm ring-1 ${all ? "opacity-70 ring-emerald-200" : "ring-slate-200"}`}>
              <span className="relative shrink-0">
                {first.image_url ? <img src={first.image_url} alt="" className="h-16 w-16 rounded object-contain" /> : <span className="flex h-16 w-16 items-center justify-center rounded bg-slate-100 text-slate-400">?</span>}
                <span className={`absolute right-0 top-0 rounded px-1 text-[11px] font-semibold tabular-nums ${all ? "bg-emerald-600 text-white" : "bg-white/90 text-slate-800 ring-1 ring-slate-200"}`}>{all ? "✓ " : ""}{done}/{us.length}</span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-slate-900">{first.name}
                  {first.product_id && <CompositionBadgeLive productId={first.product_id} productName={first.name} />}</span>
                <span className="block text-xs text-slate-500">{first.product_type_slug ?? ""}
                  {first.assembled_qty > 0 && <b className="text-emerald-700"> · {t("rec.assembledN", { k: Math.min(first.assembled_qty, us.length), n: us.length })}</b>}</span>
                <span className="block font-mono text-[11px] text-slate-500">{[first.product_id, sup].filter(Boolean).join(" · ")}</span>
                <span className="block text-[11px] text-slate-500">{[first.order_id ? t("rec.po", { n: first.order_id }) : null, first.supplier].filter(Boolean).join(" · ")}</span>
                <span className="mt-1 flex flex-wrap gap-1.5">
                  {!all && (
                    <button onClick={() => label.mutate(first.row)} disabled={label.isPending} className="rounded border border-orange-300 px-2 py-0.5 text-xs text-orange-800">{t("rec.labelOne")}</button>
                  )}
                  {done > 0 && (
                    <button onClick={() => reprint.mutate(us.flatMap((u) => (u.piece_id ? [u.piece_id] : [])))} disabled={reprint.isPending}
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700">🖨 {t("rec.reprint")}</button>
                  )}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// His composition badge on a line card: the product's saved parts (or the sofa suggestion), as a button.
function CompositionBadgeLive({ productId, productName }: { productId: number; productName: string | null }) {
  const { t } = useT();
  const c = useQuery({ queryKey: ["composition", productId], queryFn: () => fetchComposition(productId), staleTime: 5 * 60_000 });
  const parts = partsOf(t, c.data).length;
  return <CompositionBadge productId={productId} productName={productName} parts={parts} suggested={c.data?.suggested} />;
}
